import React, { useState, useEffect, useMemo } from "react";
import api from "../../services/api";
import { getUserTimezone } from "../../utils/timezone";
import { COLUMN_PRESETS } from "./ColumnPresets";
import ReportViewSummaryCard from "../reports/templates/ReportViewSummaryCard";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FORMATS = [
  { key: "xlsx", label: "Excel (.xlsx)", hint: "Best for spreadsheets" },
  { key: "csv", label: "CSV (.csv)", hint: "Plain text, universal" },
];

const FREQUENCIES = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

const WEEKDAYS = [
  { key: 0, label: "Sunday" },
  { key: 1, label: "Monday" },
  { key: 2, label: "Tuesday" },
  { key: 3, label: "Wednesday" },
  { key: 4, label: "Thursday" },
  { key: 5, label: "Friday" },
  { key: 6, label: "Saturday" },
];

const DATE_RANGES = [
  { key: "all", label: "All time" },
  { key: "last_7", label: "Last 7 days" },
  { key: "last_14", label: "Last 14 days" },
  { key: "last_30", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "last_quarter", label: "Last quarter" },
  { key: "last_6_months", label: "Last 6 months" },
  { key: "this_year", label: "This year" },
  { key: "last_year", label: "Last year" },
  { key: "custom", label: "Custom range" },
];

const DATE_FIELDS = [
  { key: "created", label: "Created date" },
  { key: "updated", label: "Last updated" },
  { key: "demo_date", label: "Demo date" },
  { key: "contact_date", label: "Contact date" },
];

const COMMON_TIMEZONES = [
  "UTC",
  "Asia/Calcutta",
  "Asia/Dubai",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Australia/Sydney",
];

function parseRecipients(raw) {
  return String(raw || "")
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function validate(form) {
  const errs = {};
  if (!form.name.trim()) errs.name = "Name is required.";
  else if (form.name.length > 160) errs.name = "Max 160 characters.";

  const recipients = parseRecipients(form.recipients);
  /* Recipients are optional for on-demand, required for scheduled. */
  if (form.frequency !== "on_demand" && recipients.length === 0) {
    errs.recipients =
      "At least one recipient email is required for scheduled reports.";
  } else if (recipients.length > 0) {
    const bad = recipients.filter((e) => !EMAIL_RE.test(e));
    if (bad.length) {
      errs.recipients = `Invalid email${bad.length > 1 ? "s" : ""}: ${bad.join(", ")}`;
    }
  }

  if (form.frequency !== "on_demand") {
    if (!/^\d{2}:\d{2}$/.test(form.time_of_day)) {
      errs.time_of_day = "Time must be HH:MM.";
    }
    if (!String(form.timezone || "").trim()) {
      errs.timezone = "Timezone is required.";
    }
  }

  if (form.date_range === "custom") {
    if (!form.date_from || !form.date_to) {
      errs.date_range = "Both From and To are required for a custom range.";
    } else if (form.date_from > form.date_to) {
      errs.date_range = "From must be before To.";
    }
  }

  return errs;
}

export default function ScheduleExportModal({
  open,
  currentFilters,
  defaultName,
  defaultSavedViewId,
  editRow,
  onClose,
  onCreated,
}) {
  const isEdit = !!editRow;

  const [form, setForm] = useState({
    name: "",
    format: "xlsx",
    frequency: "on_demand",
    day_of_week: 1,
    day_of_month: 1,
    time_of_day: "09:00",
    timezone: getUserTimezone() || "UTC",
    recipients: "",
    reminder_enabled: true,
    saved_view_id: "",
    template_type: "default",
    template_key: "",
    date_range: "all",
    date_field: "created",
    date_from: "",
    date_to: "",
  });

  const [savedViews, setSavedViews] = useState([]);
  const [reportTemplates, setReportTemplates] = useState([]);

  const presetViews = Object.entries(COLUMN_PRESETS).map(([key, preset]) => ({
    id: `preset:${key}`,
    name: preset.label,
    presetKey: key,
    isPreset: true,
  }));

  const [savedViewsError, setSavedViewsError] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverError, setServerError] = useState("");

  /* Load both saved views and report templates when modal opens */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [viewsRes, tplRes] = await Promise.all([
          api.getSavedViews?.({ is_report_template: 0 }),
          api.getSavedViews?.({ is_report_template: 1 }),
        ]);
        const views = Array.isArray(viewsRes?.data) ? viewsRes.data : [];
        const tpls = Array.isArray(tplRes?.data) ? tplRes.data : [];
        if (cancelled) return;
        setSavedViews(views.filter((v) => !v.is_report_template));
        setReportTemplates(tpls.filter((v) => v.is_report_template));
        setSavedViewsError("");
      } catch {
        if (!cancelled)
          setSavedViewsError("Failed to load views and templates.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  /* Hydrate the form when the modal opens */
  useEffect(() => {
    if (!open) return;

    if (editRow) {
      const f = editRow.filters || {};
      const hasView = !!editRow.saved_view_id;
      setForm({
        name: editRow.name || "",
        format: editRow.format || "xlsx",
        frequency: editRow.frequency || "on_demand",
        day_of_week: editRow.day_of_week ?? 1,
        day_of_month: editRow.day_of_month ?? 1,
        time_of_day: editRow.time_of_day || "09:00",
        timezone: editRow.timezone || getUserTimezone() || "UTC",
        recipients: (editRow.recipients || []).join(", "),
        reminder_enabled: editRow.reminder_enabled !== false,
        saved_view_id: hasView ? String(editRow.saved_view_id) : "",
        template_type: hasView ? "saved_view" : "default",
        template_key: "",
        date_range: f.dateRange || "all",
        date_field: f.dateField || "created",
        date_from: f.dateFrom || "",
        date_to: f.dateTo || "",
      });
    } else {
      setForm({
        name: defaultName || "",
        format: "xlsx",
        frequency: "on_demand",
        day_of_week: 1,
        day_of_month: 1,
        time_of_day: "09:00",
        timezone: getUserTimezone() || "UTC",
        recipients: "",
        reminder_enabled: true,
        saved_view_id: defaultSavedViewId ? String(defaultSavedViewId) : "",
        template_type: defaultSavedViewId ? "saved_view" : "default",
        template_key: "",
        date_range: "all",
        date_field: "created",
        date_from: "",
        date_to: "",
      });
    }

    setFieldErrors({});
    setServerError("");
    setSaving(false);
  }, [open, editRow, defaultName, defaultSavedViewId]);

  const recipientCount = useMemo(
    () => parseRecipients(form.recipients).length,
    [form.recipients],
  );

  if (!open) return null;

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => ({ ...fe, [k]: undefined }));
    setServerError("");
  };

  /* Single source of truth for the template dropdown value */
  const selectValue = (() => {
    if (form.template_type === "preset") return `preset:${form.template_key}`;
    if (form.template_type === "saved_view" && form.saved_view_id) {
      return String(form.saved_view_id);
    }
    return "";
  })();

  const handleTemplateSelect = (e) => {
    const value = e.target.value;

    if (!value) {
      setForm((f) => ({
        ...f,
        saved_view_id: "",
        template_type: "default",
        template_key: "",
      }));
    } else if (value.startsWith("preset:")) {
      setForm((f) => ({
        ...f,
        saved_view_id: "",
        template_type: "preset",
        template_key: value.slice("preset:".length),
      }));
    } else {
      setForm((f) => ({
        ...f,
        saved_view_id: String(value),
        template_type: "saved_view",
        template_key: "",
      }));
    }

    setServerError("");
  };

  const save = async () => {
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }
    const recipients = parseRecipients(form.recipients);
    setSaving(true);
    setServerError("");

    try {
      const filters = {
        ...(currentFilters || {}),
        dateRange: form.date_range,
        dateField: form.date_field,
        dateFrom: form.date_range === "custom" ? form.date_from : null,
        dateTo: form.date_range === "custom" ? form.date_to : null,
      };

      /* Only attach saved_view_id when a real view/template is picked */
      const isSavedView =
        form.template_type === "saved_view" && !!form.saved_view_id;

      const payload = {
        name: form.name.trim(),
        entity: "leads",
        format: form.format,
        frequency: form.frequency,
        day_of_week:
          form.frequency === "weekly" ? Number(form.day_of_week) : null,
        day_of_month:
          form.frequency === "monthly" ? Number(form.day_of_month) : null,
        time_of_day: form.frequency === "on_demand" ? null : form.time_of_day,
        timezone: form.timezone,
        recipients,
        reminder_enabled:
          form.frequency === "on_demand" ? false : form.reminder_enabled,
        saved_view_id: isSavedView ? Number(form.saved_view_id) : null,
        filters: {
          ...filters,
          reportPreset:
            form.template_type === "preset" ? form.template_key : null,
        },
      };

      if (isEdit) {
        await api.updateScheduledExport(editRow.id, payload);
      } else {
        await api.createScheduledExport(payload);
      }
      onCreated?.();
      onClose?.();
    } catch (e) {
      const data = e?.response?.data;
      if (data?.error === "INVALID_PAYLOAD" && Array.isArray(data.errors)) {
        setServerError(data.errors.join(" "));
      } else {
        setServerError(
          data?.error ||
            data?.message ||
            (isEdit ? "Failed to update report." : "Failed to create report."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const isOnDemand = form.frequency === "on_demand";

  /* Find the currently selected view/template for the summary card */
  const selectedView = (() => {
    if (!form.saved_view_id) return null;
    return (
      reportTemplates.find(
        (v) => String(v.id) === String(form.saved_view_id),
      ) ||
      savedViews.find((v) => String(v.id) === String(form.saved_view_id)) ||
      null
    );
  })();

  return (
    <div className="modal-overlay" role="presentation" onMouseDown={onClose}>
      <div
        className="modal r6-sch-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="r6-sch-head">
          <div>
            <div className="r6-sch-eyebrow">
              {isEdit ? "Edit Report" : "New Report"}
            </div>
            <h2 className="r6-sch-title">
              {isEdit ? editRow?.name || "Report" : "Create a Report"}
            </h2>
            <p className="r6-sch-sub">
              {isEdit
                ? "Update what this report contains and how it is delivered."
                : "Pick a saved view, choose a date range, then download, email or schedule."}
            </p>
          </div>
          <button
            type="button"
            className="r6-sch-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="r6-sch-body">
          {/* ============ Section 1 — Template ============ */}
          <section className="r6-sch-section">
            <div className="r6-sch-section-head">
              <span className="r6-sch-section-num">1</span>
              <div>
                <div className="r6-sch-section-title">Template</div>
                <div className="r6-sch-section-sub">
                  Pick a report template, preset, or saved view to define
                  columns and filters.
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Saved Views / Templates</label>
              <select
                className="form-control"
                value={selectValue}
                onChange={handleTemplateSelect}
              >
                <option value="">Default</option>

                {reportTemplates.length > 0 && (
                  <optgroup label="Report Templates">
                    {reportTemplates.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.name}
                        {v.is_default ? " ★" : ""}
                      </option>
                    ))}
                  </optgroup>
                )}

                <optgroup label="Presets">
                  {presetViews.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </optgroup>

                {savedViews.length > 0 && (
                  <optgroup label="Saved Views">
                    {savedViews.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.name}
                        {v.is_default ? " ★" : ""}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {savedViewsError ? (
                <div className="field-error">{savedViewsError}</div>
              ) : form.saved_view_id ? (
                <div className="field-hint">
                  Columns and filters come from this selection. Editing it later
                  updates this report.
                </div>
              ) : form.template_type === "preset" ? (
                <div className="field-hint">
                  Built-in column preset — layout only.
                </div>
              ) : (
                <div className="field-hint">
                  Default — uses your team's standard column layout and active
                  filters.
                </div>
              )}
            </div>

            {/* Read-only summary of the current selection */}
            {form.template_type === "preset" &&
            form.template_key &&
            COLUMN_PRESETS[form.template_key] ? (
              <ReportViewSummaryCard
                preset={COLUMN_PRESETS[form.template_key]}
              />
            ) : selectedView ? (
              <ReportViewSummaryCard view={selectedView} />
            ) : null}
          </section>

          {/* ============ Section 2 — Data Range ============ */}
          <section className="r6-sch-section">
            <div className="r6-sch-section-head">
              <span className="r6-sch-section-num">2</span>
              <div>
                <div className="r6-sch-section-title">Data Range</div>
                <div className="r6-sch-section-sub">
                  Which leads to include.
                </div>
              </div>
            </div>

            <div className="r6-sch-grid">
              <div className="form-group">
                <label>Range</label>
                <select
                  className="form-control"
                  value={form.date_range}
                  onChange={set("date_range")}
                >
                  {DATE_RANGES.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Based on</label>
                <select
                  className="form-control"
                  value={form.date_field}
                  onChange={set("date_field")}
                >
                  {DATE_FIELDS.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>

              {form.date_range === "custom" && (
                <>
                  <div className="form-group">
                    <label>From</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.date_from}
                      onChange={set("date_from")}
                    />
                  </div>
                  <div className="form-group">
                    <label>To</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.date_to}
                      onChange={set("date_to")}
                    />
                  </div>
                </>
              )}
            </div>

            {fieldErrors.date_range && (
              <div className="field-error">{fieldErrors.date_range}</div>
            )}
          </section>

          {/* ============ Section 3 — Delivery ============ */}
          <section className="r6-sch-section">
            <div className="r6-sch-section-head">
              <span className="r6-sch-section-num">3</span>
              <div>
                <div className="r6-sch-section-title">Delivery</div>
                <div className="r6-sch-section-sub">
                  Name, file format, and whether it runs on a schedule.
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>
                Name <span className="r6-req">*</span>
              </label>
              <input
                className={`form-control ${fieldErrors.name ? "error" : ""}`}
                value={form.name}
                onChange={set("name")}
                placeholder="Q3 Won Deals"
                maxLength={160}
              />
              {fieldErrors.name ? (
                <div className="field-error">{fieldErrors.name}</div>
              ) : (
                <div className="field-hint">
                  A short, memorable name — visible only to you.
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Format</label>
              <div className="r6-sch-segmented">
                {FORMATS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`r6-sch-seg ${
                      form.format === f.key ? "is-active" : ""
                    }`}
                    onClick={() => setForm((p) => ({ ...p, format: f.key }))}
                  >
                    <span className="r6-sch-seg-label">{f.label}</span>
                    <span className="r6-sch-seg-hint">{f.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Mode</label>
              <div className="r6-sch-segmented">
                <button
                  type="button"
                  className={`r6-sch-seg ${isOnDemand ? "is-active" : ""}`}
                  onClick={() =>
                    setForm((p) => ({ ...p, frequency: "on_demand" }))
                  }
                >
                  <span className="r6-sch-seg-label">On demand</span>
                  <span className="r6-sch-seg-hint">
                    Run manually — download or email now
                  </span>
                </button>
                <button
                  type="button"
                  className={`r6-sch-seg ${!isOnDemand ? "is-active" : ""}`}
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      frequency:
                        p.frequency === "on_demand" ? "weekly" : p.frequency,
                    }))
                  }
                >
                  <span className="r6-sch-seg-label">Scheduled</span>
                  <span className="r6-sch-seg-hint">
                    Delivered automatically on a schedule
                  </span>
                </button>
              </div>
            </div>
          </section>

          {/* ============ Section 4 — Schedule (only if Scheduled) ============ */}
          {!isOnDemand && (
            <section className="r6-sch-section">
              <div className="r6-sch-section-head">
                <span className="r6-sch-section-num">4</span>
                <div>
                  <div className="r6-sch-section-title">Schedule</div>
                  <div className="r6-sch-section-sub">
                    When this report is delivered automatically.
                  </div>
                </div>
              </div>

              <div className="r6-sch-grid">
                <div className="form-group">
                  <label>Frequency</label>
                  <select
                    className="form-control"
                    value={form.frequency}
                    onChange={set("frequency")}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>

                {form.frequency === "weekly" && (
                  <div className="form-group">
                    <label>Day of week</label>
                    <select
                      className="form-control"
                      value={form.day_of_week}
                      onChange={set("day_of_week")}
                    >
                      {WEEKDAYS.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {form.frequency === "monthly" && (
                  <div className="form-group">
                    <label>Day of month</label>
                    <select
                      className="form-control"
                      value={form.day_of_month}
                      onChange={set("day_of_month")}
                    >
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>
                    Time <span className="r6-req">*</span>
                  </label>
                  <input
                    className={`form-control ${
                      fieldErrors.time_of_day ? "error" : ""
                    }`}
                    type="time"
                    value={form.time_of_day}
                    onChange={set("time_of_day")}
                  />
                  {fieldErrors.time_of_day && (
                    <div className="field-error">{fieldErrors.time_of_day}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Timezone <span className="r6-req">*</span>
                  </label>
                  <input
                    className={`form-control ${
                      fieldErrors.timezone ? "error" : ""
                    }`}
                    list="r6-sch-tz"
                    value={form.timezone}
                    onChange={set("timezone")}
                  />
                  <datalist id="r6-sch-tz">
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz} />
                    ))}
                  </datalist>
                  {fieldErrors.timezone && (
                    <div className="field-error">{fieldErrors.timezone}</div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* ============ Section 5 — Recipients ============ */}
          <section className="r6-sch-section">
            <div className="r6-sch-section-head">
              <span className="r6-sch-section-num">{isOnDemand ? 4 : 5}</span>
              <div>
                <div className="r6-sch-section-title">Recipients</div>
                <div className="r6-sch-section-sub">
                  {isOnDemand
                    ? "Who receives this when you click Email now."
                    : "Who receives the scheduled delivery."}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>
                Recipients{" "}
                {isOnDemand ? (
                  <span className="r6-optional">(optional)</span>
                ) : (
                  <span className="r6-req">*</span>
                )}
              </label>
              <input
                className={`form-control ${
                  fieldErrors.recipients ? "error" : ""
                }`}
                value={form.recipients}
                onChange={set("recipients")}
                placeholder="you@example.com, team@example.com"
              />
              {fieldErrors.recipients ? (
                <div className="field-error">{fieldErrors.recipients}</div>
              ) : (
                <div className="field-hint">
                  {recipientCount > 0
                    ? `${recipientCount} recipient${
                        recipientCount === 1 ? "" : "s"
                      } — comma-separated`
                    : isOnDemand
                      ? "Optional. Add at least one email to enable 'Email now'. Leave blank for download-only."
                      : "Comma-separated email addresses"}
                </div>
              )}
            </div>

            {!isOnDemand && (
              <label className="r6-sch-toggle">
                <input
                  type="checkbox"
                  checked={form.reminder_enabled}
                  onChange={set("reminder_enabled")}
                />
                <div>
                  <div className="r6-sch-toggle-title">
                    Send reminder 1 hour before
                  </div>
                  <div className="r6-sch-toggle-hint">
                    You will receive a bell notification before each run.
                  </div>
                </div>
              </label>
            )}
          </section>

          {serverError && <div className="error-message">{serverError}</div>}
        </div>

        <div className="r6-sch-foot">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={saving}
          >
            {saving
              ? "Saving…"
              : isEdit
                ? "Save Changes"
                : isOnDemand
                  ? "Create Report"
                  : "Create Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
